import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { getAuthErrorMessage } from '@utils/authErrors';
import PageBackground from '@components/PageBackground';
import SeoHead from '@components/SeoHead';


const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, userRole, currentUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect when user role is determined after login
  useEffect(() => {
    if (currentUser && userRole) {
      const redirectPath = userRole === 'landlord' ? '/landlord/dashboard' : '/tenant/swipe';
      navigate(redirectPath);
    }
  }, [currentUser, userRole, navigate]);

  // Fallback: if sign-in succeeds but the role never resolves, don't leave the
  // user staring at a spinner with no feedback.
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => {
      if (currentUser && !userRole) {
        setError('We signed you in but could not load your account. Please try again.');
        setLoading(false);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [loading, currentUser, userRole]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      // Keep loading until the redirect effect fires (or the fallback times out).
    } catch (err) {
      setError(getAuthErrorMessage(err, 'Failed to sign in. Please check your credentials.'));
      console.error(err);
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      await loginWithGoogle();
      // Keep loading until the redirect effect fires (or the fallback times out).
    } catch (err) {
      setError(getAuthErrorMessage(err, err instanceof Error ? err.message : 'Failed to sign in with Google.'));
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-pink-200 selection:text-pink-900 scroll-smooth flex items-center justify-center px-4">
      <SeoHead
        title="Log in"
        description="Log in to RoomZa to continue matching with roommates and managing your shared-home search in South Africa."
        path="/login"
      />
      <PageBackground />

      <div className="max-w-md w-full relative z-10">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center mb-4">
            <img src="/logo.png" alt="RoomZa Logo" className="w-16 h-16 rounded-xl" />
          </Link>
          <h1 className="text-5xl font-extrabold text-gray-900 mb-2">
            Welcome Back
          </h1>
          <p className="text-text-secondary text-lg">Sign in to continue your journey</p>
        </div>

        <div className="bg-white/60 backdrop-blur-md rounded-3xl p-8 border border-white/50 shadow-xl shadow-pink-100/50">
          {error && (
            <div role="alert" className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-600 text-sm font-medium">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-text-primary mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-text-primary mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 px-8 rounded-xl bg-gradient-to-r from-pink-500 to-rose-600 text-white font-bold text-lg hover:shadow-xl hover:shadow-pink-200 hover:scale-105 transition-all"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-text-muted font-medium">Or continue with</span>
              </div>
            </div>

            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full h-14 px-8 rounded-xl border-2 border-pink-100 bg-white text-pink-600 font-bold text-lg hover:border-pink-200 hover:bg-pink-50 transition-all flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2 inline" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
          </div>

          <p className="mt-8 text-center text-text-secondary text-sm">
            Don't have an account?{' '}
            <Link
              to="/signup"
              className="text-pink-600 hover:text-pink-700 font-semibold transition-colors"
            >
              Sign up
            </Link>
          </p>
        </div>

        <p className="mt-6 text-center text-text-muted text-sm">
          By continuing, you agree to our{' '}
          <Link to="/legal/terms" className="text-pink-600 hover:underline">Terms</Link>
          {' '}and{' '}
          <Link to="/legal/privacy" className="text-pink-600 hover:underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
